import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import clsx from 'clsx';
import {
  ArrowLeft,
  CheckCircle2,
  Copy,
  Download,
  Hash,
  Mail,
  Megaphone,
  Package,
  Pencil,
  Plus,
  Save,
  Sparkles,
  Trash2,
  X,
} from 'lucide-react';

// Inline brand glyphs for the social platforms — installed lucide-react predates
// the brand-icon expansion so we draw the "in", "𝕏", and "f" marks ourselves.
const Linkedin = (props) => (
  <svg viewBox="0 0 24 24" fill="currentColor" {...props}>
    <path d="M20.45 20.45h-3.55v-5.57c0-1.33-.03-3.04-1.85-3.04-1.85 0-2.13 1.45-2.13 2.94v5.67H9.36V9h3.41v1.56h.05c.48-.9 1.64-1.85 3.37-1.85 3.6 0 4.27 2.37 4.27 5.45v6.29zM5.34 7.43a2.07 2.07 0 11.01-4.14 2.07 2.07 0 01-.01 4.14zM3.56 20.45h3.56V9H3.56v11.45zM22.23 0H1.77C.79 0 0 .77 0 1.72v20.56C0 23.23.79 24 1.77 24h20.46c.98 0 1.77-.77 1.77-1.72V1.72C24 .77 23.21 0 22.23 0z" />
  </svg>
);
const Twitter = (props) => (
  <svg viewBox="0 0 24 24" fill="currentColor" {...props}>
    <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24h-6.66l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.16 17.52h1.833L7.084 4.126H5.117l11.967 15.644z" />
  </svg>
);
const Facebook = (props) => (
  <svg viewBox="0 0 24 24" fill="currentColor" {...props}>
    <path d="M24 12.073C24 5.405 18.627 0 12 0S0 5.405 0 12.073C0 18.1 4.388 23.094 10.125 24v-8.437H7.078v-3.49h3.047V9.413c0-3.022 1.79-4.69 4.533-4.69 1.313 0 2.686.235 2.686.235v2.97h-1.513c-1.49 0-1.956.93-1.956 1.886v2.266h3.328l-.532 3.49h-2.796V24C19.612 23.094 24 18.1 24 12.073z" />
  </svg>
);
import { api } from '../../api/client';
import EmptyState from '../../components/EmptyState';
import InfoTip from '../../components/InfoTip';
import { useAuthStore } from '../../store/auth';
import { formatRelative } from '../../lib/format';
import {
  DEFAULT_SERVICE_MENU,
  EMAIL_TEMPLATES,
  SOCIAL_TEMPLATES,
  VAR_META,
  extractVars,
  getEmailTemplate,
  getSocialTemplate,
  substitute,
} from '../../constants/salesTemplates';

const MODES = [
  { id: 'email', label: 'Cold email', icon: Mail },
  { id: 'social', label: 'Social posts', icon: Megaphone },
  { id: 'service-menu', label: 'Service menu', icon: Package },
];

const PLATFORM_ICON = {
  linkedin: Linkedin,
  twitter: Twitter,
  facebook: Facebook,
};
const PLATFORM_COLOR = {
  linkedin: '#0a66c2',
  twitter: '#1da1f2',
  facebook: '#1877f2',
};

const CURRENCY_SYMBOL = { USD: '$', EUR: '€', GBP: '£', INR: '₹' };

export default function SalesTab({ domain }) {
  const [mode, setMode] = useState('email');

  return (
    <div className="space-y-6">
      <SalesHero />

      <div className="flex gap-1 overflow-x-auto scroll-soft border-b border-gray-100 pb-px">
        {MODES.map((m) => (
          <button
            key={m.id}
            onClick={() => setMode(m.id)}
            className={clsx(
              'flex items-center gap-2 px-4 py-2.5 text-sm font-medium border-b-2 whitespace-nowrap transition-colors',
              mode === m.id
                ? 'border-brand-600 text-brand-700'
                : 'border-transparent text-ink-400 hover:text-ink-900',
            )}
          >
            <m.icon className="w-4 h-4" />
            {m.label}
          </button>
        ))}
      </div>

      {mode === 'email' && <EmailMode domain={domain} />}
      {mode === 'social' && <SocialMode domain={domain} />}
      {mode === 'service-menu' && <ServiceMenuMode />}
    </div>
  );
}

function SalesHero() {
  return (
    <div className="relative rounded-2xl overflow-hidden bg-ink-gradient text-white p-6 sm:p-8 shadow-xl">
      <div className="absolute inset-0 opacity-30 bg-brand-radial" />
      <div className="absolute inset-0 bg-grid opacity-20" />
      <div className="absolute -top-20 -right-20 w-72 h-72 rounded-full bg-brand-600 blur-3xl opacity-30" />
      <div className="relative">
        <div className="text-xs uppercase tracking-wider text-white/60 mb-1 flex items-center gap-1.5">
          <Megaphone className="w-3 h-3" /> Sales & marketing toolkit
        </div>
        <h2 className="text-2xl sm:text-3xl font-bold inline-flex items-center gap-2">
          Win your next client this week
          <span className="text-white/70">
            <InfoTip
              title="How variables work"
              body="Templates use {{placeholders}} like {{prospectName}} and {{score}} that get filled in from your audit data and the lead's domain. Edit a template once, render it for any prospect."
            />
          </span>
        </h2>
        <p className="text-sm text-white/70 mt-1 max-w-xl">
          Cold-email templates, social posts, and a service menu — all editable, all
          variable-driven. Personalize once, copy/download in seconds.
        </p>
      </div>
    </div>
  );
}

/* =====================  EMAIL MODE  ===================== */

function useDefaultVars(domain) {
  const { user } = useAuthStore();
  const { data: brand } = useQuery({
    queryKey: ['brand-profile'],
    queryFn: () => api.get('/api/me/brand-profile').then((r) => r.data.profile),
  });
  return useMemo(
    () => ({
      agencyName: brand?.agencyName || '',
      senderName: user?.name || '',
      senderEmail: user?.email || '',
      senderTitle: brand?.tagline || '',
      prospectDomain: domain?.domain || '',
      auditLink: brand?.contactUrl || '',
    }),
    [brand, user, domain],
  );
}

function EmailMode({ domain }) {
  const qc = useQueryClient();
  const defaultVars = useDefaultVars(domain);
  const [active, setActive] = useState(null); // either { template_id, savedId? } or null
  const [vars, setVars] = useState({});
  const [name, setName] = useState('');
  const [subjectOverride, setSubjectOverride] = useState('');
  const [bodyOverride, setBodyOverride] = useState('');
  const [saveError, setSaveError] = useState('');

  const savedQuery = useQuery({
    queryKey: ['sales-assets', 'email'],
    queryFn: () =>
      api.get('/api/sales-assets?kind=email').then((r) => r.data.assets),
  });

  const saveMutation = useMutation({
    mutationFn: (body) =>
      active?.savedId
        ? api.put(`/api/sales-assets/${active.savedId}`, body).then((r) => r.data.asset)
        : api
            .post('/api/sales-assets', { kind: 'email', ...body })
            .then((r) => r.data.asset),
    onSuccess: (asset) => {
      qc.invalidateQueries({ queryKey: ['sales-assets', 'email'] });
      setActive({ template_id: asset.template_id, savedId: asset._id });
      setSaveError('');
    },
    onError: (err) =>
      setSaveError(err.response?.data?.error || 'Could not save asset'),
  });

  const deleteMutation = useMutation({
    mutationFn: (id) => api.delete(`/api/sales-assets/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['sales-assets', 'email'] }),
  });

  const tpl = active ? getEmailTemplate(active.template_id) : null;
  const varKeys = tpl ? extractVars(tpl) : [];

  const startNew = (templateId) => {
    const t = getEmailTemplate(templateId);
    setActive({ template_id: templateId });
    setName(`${t.label}`);
    setSubjectOverride(t.subject);
    setBodyOverride(t.body);
    const initial = { ...defaultVars };
    for (const k of extractVars(t)) {
      if (!(k in initial)) initial[k] = '';
    }
    setVars(initial);
    setSaveError('');
  };

  const startEdit = (saved) => {
    const t = getEmailTemplate(saved.template_id);
    if (!t) return;
    setActive({ template_id: saved.template_id, savedId: saved._id });
    setName(saved.name);
    setSubjectOverride(saved.payload?.subject ?? t.subject);
    setBodyOverride(saved.payload?.body ?? t.body);
    setVars({ ...defaultVars, ...(saved.payload?.vars || {}) });
    setSaveError('');
  };

  const onSave = () => {
    saveMutation.mutate({
      template_id: active.template_id,
      name: name.trim() || tpl.label,
      payload: { subject: subjectOverride, body: bodyOverride, vars },
    });
  };

  if (active && tpl) {
    return (
      <EmailEditor
        template={tpl}
        savedId={active.savedId}
        name={name}
        setName={setName}
        subject={subjectOverride}
        setSubject={setSubjectOverride}
        body={bodyOverride}
        setBody={setBodyOverride}
        vars={vars}
        setVars={setVars}
        varKeys={varKeys}
        saving={saveMutation.isPending}
        saveError={saveError}
        onSave={onSave}
        onClose={() => setActive(null)}
      />
    );
  }

  const saved = savedQuery.data || [];
  return (
    <div className="space-y-6">
      {saved.length > 0 && (
        <SavedList
          title="Your saved emails"
          items={saved}
          icon={Mail}
          getLabel={(s) =>
            getEmailTemplate(s.template_id)?.label || s.template_id || 'Custom'
          }
          onOpen={startEdit}
          onDelete={(id) => {
            if (window.confirm('Delete this saved email?')) deleteMutation.mutate(id);
          }}
        />
      )}

      <div>
        <h3 className="text-base font-semibold text-ink-900 mb-3 flex items-center gap-2">
          <Sparkles className="w-4 h-4 text-brand-600" /> Email templates
        </h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {EMAIL_TEMPLATES.map((t) => (
            <button
              key={t.id}
              onClick={() => startNew(t.id)}
              className="card-hover text-left p-4 group"
            >
              <div className="flex items-center gap-3 mb-2">
                <span className="flex-none h-10 w-10 rounded-xl bg-gradient-to-br from-brand-50 to-cyan-500/10 text-brand-700 flex items-center justify-center group-hover:scale-110 transition-transform">
                  <Mail className="w-5 h-5" />
                </span>
                <div className="min-w-0">
                  <div className="font-semibold text-ink-900 truncate">{t.label}</div>
                  <div className="text-[10px] font-medium uppercase tracking-wider text-ink-400">
                    {t.audience}
                  </div>
                </div>
              </div>
              <p className="text-xs text-ink-400 leading-relaxed mb-2">
                {t.description}
              </p>
              <div className="text-xs text-ink-700 font-medium truncate">{t.subject}</div>
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}

function EmailEditor({
  template,
  savedId,
  name,
  setName,
  subject,
  setSubject,
  body,
  setBody,
  vars,
  setVars,
  varKeys,
  saving,
  saveError,
  onSave,
  onClose,
}) {
  const [copied, setCopied] = useState(null);

  const renderedSubject = substitute(subject, vars);
  const renderedBody = substitute(body, vars);

  const onCopy = async (text, key) => {
    await navigator.clipboard.writeText(text);
    setCopied(key);
    setTimeout(() => setCopied(null), 1800);
  };

  const onDownload = () => {
    const txt = `Subject: ${renderedSubject}\n\n${renderedBody}`;
    const blob = new Blob([txt], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${name.replace(/[^a-z0-9-_]+/gi, '-').toLowerCase() || 'email'}.txt`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-3 min-w-0">
          <button className="btn-icon" onClick={onClose}>
            <ArrowLeft className="w-4 h-4" />
          </button>
          <span className="flex-none h-10 w-10 rounded-xl bg-gradient-to-br from-brand-50 to-cyan-500/10 text-brand-700 flex items-center justify-center">
            <Mail className="w-5 h-5" />
          </span>
          <div className="min-w-0">
            <div className="text-xs uppercase tracking-wide text-ink-400">
              {savedId ? 'Editing email' : 'New email from template'}
            </div>
            <div className="font-semibold text-ink-900 truncate">{template.label}</div>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button
            className="btn-secondary"
            onClick={() => onCopy(renderedSubject, 'subject')}
          >
            {copied === 'subject' ? (
              <CheckCircle2 className="w-4 h-4" />
            ) : (
              <Copy className="w-4 h-4" />
            )}
            {copied === 'subject' ? 'Copied' : 'Copy subject'}
          </button>
          <button
            className="btn-secondary"
            onClick={() => onCopy(renderedBody, 'body')}
          >
            {copied === 'body' ? (
              <CheckCircle2 className="w-4 h-4" />
            ) : (
              <Copy className="w-4 h-4" />
            )}
            {copied === 'body' ? 'Copied' : 'Copy body'}
          </button>
          <button className="btn-secondary" onClick={onDownload}>
            <Download className="w-4 h-4" /> Download
          </button>
          <button className="btn-primary" onClick={onSave} disabled={saving}>
            <Save className="w-4 h-4" />
            {saving ? 'Saving…' : savedId ? 'Update' : 'Save'}
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="card">
          <div className="section-header">
            <div className="section-title">
              <span className="section-icon">
                <Pencil className="w-4 h-4" />
              </span>
              Edit
            </div>
          </div>

          <div className="space-y-4">
            <div>
              <label className="label">Asset name (internal)</label>
              <input
                className="input"
                value={name}
                onChange={(e) => setName(e.target.value)}
              />
            </div>

            <div>
              <label className="label">Subject</label>
              <input
                className="input"
                value={subject}
                onChange={(e) => setSubject(e.target.value)}
              />
            </div>

            <div>
              <label className="label">Body</label>
              <textarea
                className="input min-h-[280px] font-mono text-xs"
                value={body}
                onChange={(e) => setBody(e.target.value)}
              />
              <p className="text-xs text-ink-400 mt-1.5">
                Use <code>{'{{variable}}'}</code> tokens — they get filled from the variables
                panel below.
              </p>
            </div>

            <div className="border-t border-gray-100 pt-4">
              <div className="text-xs uppercase tracking-wide font-semibold text-ink-400 mb-2">
                Variables
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {varKeys.map((key) => {
                  const meta = VAR_META[key] || { label: key, placeholder: '' };
                  return (
                    <div key={key}>
                      <label className="label flex items-center gap-1 normal-case tracking-normal text-[11px]">
                        <Hash className="w-3 h-3 text-ink-400" />
                        {meta.label}
                      </label>
                      <input
                        className="input"
                        value={vars[key] || ''}
                        placeholder={meta.placeholder}
                        onChange={(e) =>
                          setVars({ ...vars, [key]: e.target.value })
                        }
                      />
                    </div>
                  );
                })}
              </div>
            </div>

            {saveError && (
              <div className="rounded-lg bg-rose-50 border border-rose-100 text-rose-700 text-sm p-3">
                {saveError}
              </div>
            )}
          </div>
        </div>

        <div className="card">
          <div className="section-header">
            <div className="section-title">
              <span className="section-icon">
                <Sparkles className="w-4 h-4" />
              </span>
              Preview
            </div>
          </div>

          <div className="rounded-xl border border-gray-100 overflow-hidden">
            <div className="bg-gray-50 border-b border-gray-100 px-4 py-2.5">
              <div className="text-[10px] uppercase tracking-wide font-semibold text-ink-400 mb-0.5">
                Subject
              </div>
              <div className="text-sm font-semibold text-ink-900 truncate">
                {renderedSubject}
              </div>
            </div>
            <div className="p-4 text-sm text-ink-800 leading-relaxed whitespace-pre-wrap font-sans">
              {renderedBody}
            </div>
          </div>
          {(template.ctaLabel || template.ctaUrl) && (
            <div className="mt-3 text-xs text-ink-400">
              Suggested CTA: <span className="font-medium text-ink-700">
                {substitute(template.ctaLabel, vars) || '—'}
              </span>{' '}
              →{' '}
              <span className="font-mono">{substitute(template.ctaUrl, vars) || '—'}</span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

/* =====================  SOCIAL MODE  ===================== */

function SocialMode({ domain }) {
  const qc = useQueryClient();
  const defaultVars = useDefaultVars(domain);
  const [active, setActive] = useState(null);
  const [name, setName] = useState('');
  const [bodyOverride, setBodyOverride] = useState('');
  const [vars, setVars] = useState({});
  const [saveError, setSaveError] = useState('');

  const savedQuery = useQuery({
    queryKey: ['sales-assets', 'social'],
    queryFn: () =>
      api.get('/api/sales-assets?kind=social').then((r) => r.data.assets),
  });

  const saveMutation = useMutation({
    mutationFn: (body) =>
      active?.savedId
        ? api.put(`/api/sales-assets/${active.savedId}`, body).then((r) => r.data.asset)
        : api
            .post('/api/sales-assets', { kind: 'social', ...body })
            .then((r) => r.data.asset),
    onSuccess: (asset) => {
      qc.invalidateQueries({ queryKey: ['sales-assets', 'social'] });
      setActive({ template_id: asset.template_id, savedId: asset._id });
      setSaveError('');
    },
    onError: (err) =>
      setSaveError(err.response?.data?.error || 'Could not save asset'),
  });

  const deleteMutation = useMutation({
    mutationFn: (id) => api.delete(`/api/sales-assets/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['sales-assets', 'social'] }),
  });

  const tpl = active ? getSocialTemplate(active.template_id) : null;
  const varKeys = tpl ? extractVars(tpl) : [];

  // Group templates by platform — MUST be declared before the early return
  // below so React always sees the same hook count on every render
  // (Rules of Hooks). Moving it after the conditional return crashes the
  // component the first time you click a template card.
  const byPlatform = useMemo(() => {
    const m = new Map();
    for (const t of SOCIAL_TEMPLATES) {
      if (!m.has(t.platform)) m.set(t.platform, []);
      m.get(t.platform).push(t);
    }
    return m;
  }, []);

  const startNew = (templateId) => {
    const t = getSocialTemplate(templateId);
    setActive({ template_id: templateId });
    setName(t.label);
    setBodyOverride(t.body);
    const initial = { ...defaultVars };
    for (const k of extractVars(t)) {
      if (!(k in initial)) initial[k] = '';
    }
    setVars(initial);
    setSaveError('');
  };

  const startEdit = (saved) => {
    const t = getSocialTemplate(saved.template_id);
    if (!t) return;
    setActive({ template_id: saved.template_id, savedId: saved._id });
    setName(saved.name);
    setBodyOverride(saved.payload?.body ?? t.body);
    setVars({ ...defaultVars, ...(saved.payload?.vars || {}) });
    setSaveError('');
  };

  const onSave = () => {
    saveMutation.mutate({
      template_id: active.template_id,
      name: name.trim() || tpl.label,
      payload: { body: bodyOverride, vars },
    });
  };

  if (active && tpl) {
    return (
      <SocialEditor
        template={tpl}
        savedId={active.savedId}
        name={name}
        setName={setName}
        body={bodyOverride}
        setBody={setBodyOverride}
        vars={vars}
        setVars={setVars}
        varKeys={varKeys}
        saving={saveMutation.isPending}
        saveError={saveError}
        onSave={onSave}
        onClose={() => setActive(null)}
      />
    );
  }

  const saved = savedQuery.data || [];

  return (
    <div className="space-y-6">
      {saved.length > 0 && (
        <SavedList
          title="Your saved posts"
          items={saved}
          icon={Megaphone}
          getLabel={(s) =>
            getSocialTemplate(s.template_id)?.label || s.template_id || 'Custom'
          }
          onOpen={startEdit}
          onDelete={(id) => {
            if (window.confirm('Delete this saved post?')) deleteMutation.mutate(id);
          }}
        />
      )}

      {['linkedin', 'twitter', 'facebook'].map((p) => {
        const items = byPlatform.get(p);
        if (!items) return null;
        const Icon = PLATFORM_ICON[p];
        return (
          <div key={p}>
            <h3 className="text-base font-semibold text-ink-900 mb-3 flex items-center gap-2">
              <Icon className="w-4 h-4" style={{ color: PLATFORM_COLOR[p] }} />{' '}
              {p[0].toUpperCase() + p.slice(1)}
            </h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
              {items.map((t) => (
                <button
                  key={t.id}
                  onClick={() => startNew(t.id)}
                  className="card-hover text-left p-4"
                >
                  <div className="flex items-center gap-3 mb-2">
                    <span
                      className="flex-none h-10 w-10 rounded-xl text-white flex items-center justify-center"
                      style={{ backgroundColor: PLATFORM_COLOR[t.platform] }}
                    >
                      <Icon className="w-5 h-5" />
                    </span>
                    <div className="font-semibold text-ink-900">{t.label}</div>
                  </div>
                  <p className="text-xs text-ink-400 leading-relaxed line-clamp-3">
                    {t.body}
                  </p>
                </button>
              ))}
            </div>
          </div>
        );
      })}
    </div>
  );
}

function SocialEditor({
  template,
  savedId,
  name,
  setName,
  body,
  setBody,
  vars,
  setVars,
  varKeys,
  saving,
  saveError,
  onSave,
  onClose,
}) {
  const [copied, setCopied] = useState(false);
  const Icon = PLATFORM_ICON[template.platform];
  const rendered = substitute(body, vars);
  const charCount = rendered.length;
  const limit =
    template.platform === 'twitter'
      ? 280
      : template.platform === 'linkedin'
        ? 3000
        : 5000;
  const overLimit = charCount > limit;

  const onCopy = async () => {
    await navigator.clipboard.writeText(rendered);
    setCopied(true);
    setTimeout(() => setCopied(false), 1800);
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-3 min-w-0">
          <button className="btn-icon" onClick={onClose}>
            <ArrowLeft className="w-4 h-4" />
          </button>
          <span
            className="flex-none h-10 w-10 rounded-xl text-white flex items-center justify-center"
            style={{ backgroundColor: PLATFORM_COLOR[template.platform] }}
          >
            <Icon className="w-5 h-5" />
          </span>
          <div className="min-w-0">
            <div className="text-xs uppercase tracking-wide text-ink-400">
              {savedId ? 'Editing post' : 'New post'} ·{' '}
              {template.platform[0].toUpperCase() + template.platform.slice(1)}
            </div>
            <div className="font-semibold text-ink-900 truncate">{template.label}</div>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button className="btn-secondary" onClick={onCopy} disabled={overLimit}>
            {copied ? <CheckCircle2 className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
            {copied ? 'Copied' : 'Copy post'}
          </button>
          <button className="btn-primary" onClick={onSave} disabled={saving}>
            <Save className="w-4 h-4" />
            {saving ? 'Saving…' : savedId ? 'Update' : 'Save'}
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="card">
          <div className="section-header">
            <div className="section-title">
              <span className="section-icon">
                <Pencil className="w-4 h-4" />
              </span>
              Edit
            </div>
            <span
              className={clsx(
                'text-xs font-medium',
                overLimit ? 'text-rose-600' : 'text-ink-400',
              )}
            >
              {charCount} / {limit}
            </span>
          </div>
          <div className="space-y-4">
            <div>
              <label className="label">Asset name</label>
              <input
                className="input"
                value={name}
                onChange={(e) => setName(e.target.value)}
              />
            </div>
            <div>
              <label className="label">Post body</label>
              <textarea
                className="input min-h-[260px] text-sm"
                value={body}
                onChange={(e) => setBody(e.target.value)}
              />
            </div>
            {varKeys.length > 0 && (
              <div className="border-t border-gray-100 pt-4">
                <div className="text-xs uppercase tracking-wide font-semibold text-ink-400 mb-2">
                  Variables
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {varKeys.map((key) => {
                    const meta = VAR_META[key] || { label: key, placeholder: '' };
                    return (
                      <div key={key}>
                        <label className="label normal-case tracking-normal text-[11px] flex items-center gap-1">
                          <Hash className="w-3 h-3 text-ink-400" /> {meta.label}
                        </label>
                        <input
                          className="input"
                          value={vars[key] || ''}
                          placeholder={meta.placeholder}
                          onChange={(e) =>
                            setVars({ ...vars, [key]: e.target.value })
                          }
                        />
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
            {saveError && (
              <div className="rounded-lg bg-rose-50 border border-rose-100 text-rose-700 text-sm p-3">
                {saveError}
              </div>
            )}
          </div>
        </div>

        <div className="card">
          <div className="section-header">
            <div className="section-title">
              <span className="section-icon">
                <Sparkles className="w-4 h-4" />
              </span>
              Preview
            </div>
          </div>
          <div
            className="rounded-xl border p-4 text-sm leading-relaxed whitespace-pre-wrap"
            style={{
              borderColor: `${PLATFORM_COLOR[template.platform]}33`,
              backgroundColor: `${PLATFORM_COLOR[template.platform]}08`,
            }}
          >
            {rendered}
          </div>
          {template.hashtags?.length > 0 && (
            <div className="mt-3 text-xs">
              {template.hashtags.map((h) => (
                <span
                  key={h}
                  className="inline-block mr-1.5 mb-1 px-2 py-0.5 rounded bg-white border border-gray-200 text-ink-700"
                >
                  #{h}
                </span>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

/* =====================  SERVICE-MENU MODE  ===================== */

function ServiceMenuMode() {
  const qc = useQueryClient();
  const savedQuery = useQuery({
    queryKey: ['sales-assets', 'service-menu'],
    queryFn: () =>
      api.get('/api/sales-assets?kind=service-menu').then((r) => r.data.assets),
  });

  const [editing, setEditing] = useState(null); // { name, packages, savedId? } | null

  const saveMutation = useMutation({
    mutationFn: (body) =>
      editing?.savedId
        ? api.put(`/api/sales-assets/${editing.savedId}`, body).then((r) => r.data.asset)
        : api
            .post('/api/sales-assets', { kind: 'service-menu', ...body })
            .then((r) => r.data.asset),
    onSuccess: (asset) => {
      qc.invalidateQueries({ queryKey: ['sales-assets', 'service-menu'] });
      setEditing({
        savedId: asset._id,
        name: asset.name,
        packages: asset.payload?.packages || [],
      });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id) => api.delete(`/api/sales-assets/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['sales-assets', 'service-menu'] }),
  });

  const startFromDefaults = () => {
    setEditing({
      name: 'AEO services menu',
      packages: structuredClone(DEFAULT_SERVICE_MENU),
    });
  };

  const startBlank = () => {
    setEditing({ name: 'New service menu', packages: [] });
  };

  const startEdit = (saved) => {
    setEditing({
      savedId: saved._id,
      name: saved.name,
      packages: saved.payload?.packages || [],
    });
  };

  if (editing) {
    return (
      <ServiceMenuEditor
        state={editing}
        setState={setEditing}
        saving={saveMutation.isPending}
        onSave={() =>
          saveMutation.mutate({
            name: editing.name,
            payload: { packages: editing.packages },
          })
        }
        onClose={() => setEditing(null)}
      />
    );
  }

  const saved = savedQuery.data || [];

  return (
    <div className="space-y-6">
      <div className="card flex items-center justify-between gap-3 flex-wrap">
        <div>
          <div className="font-semibold text-ink-900 flex items-center gap-2">
            <Package className="w-4 h-4 text-brand-600" /> Build a service menu
          </div>
          <p className="text-sm text-ink-400 mt-1">
            Start from a 3-tier AEO template or build from scratch. Editable cards, copyable
            output.
          </p>
        </div>
        <div className="flex gap-2">
          <button className="btn-secondary" onClick={startBlank}>
            <Plus className="w-4 h-4" /> Blank
          </button>
          <button className="btn-primary" onClick={startFromDefaults}>
            <Sparkles className="w-4 h-4" /> Use default tiers
          </button>
        </div>
      </div>

      {saved.length > 0 ? (
        <SavedList
          title="Your service menus"
          items={saved}
          icon={Package}
          getLabel={(s) => `${s.payload?.packages?.length || 0} tiers`}
          onOpen={startEdit}
          onDelete={(id) => {
            if (window.confirm('Delete this menu?')) deleteMutation.mutate(id);
          }}
        />
      ) : (
        <EmptyState
          icon={Package}
          title="No service menus saved yet"
          body="Spin one up from the AEO defaults (Starter / Growth / Agency Partner), tweak pricing and features, then copy it as Markdown for proposals."
        />
      )}
    </div>
  );
}

function ServiceMenuEditor({ state, setState, saving, onSave, onClose }) {
  const [copied, setCopied] = useState(false);

  const updatePackage = (idx, patch) =>
    setState({
      ...state,
      packages: state.packages.map((p, i) => (i === idx ? { ...p, ...patch } : p)),
    });
  const removePackage = (idx) =>
    setState({ ...state, packages: state.packages.filter((_, i) => i !== idx) });
  const addPackage = () =>
    setState({
      ...state,
      packages: [
        ...state.packages,
        {
          id: `tier-${Date.now()}`,
          name: 'New tier',
          price: 0,
          currency: 'USD',
          billing: 'monthly',
          audience: '',
          description: '',
          features: [''],
          isPopular: false,
        },
      ],
    });

  const updateFeature = (pIdx, fIdx, value) => {
    const features = [...state.packages[pIdx].features];
    features[fIdx] = value;
    updatePackage(pIdx, { features });
  };
  const addFeature = (pIdx) =>
    updatePackage(pIdx, { features: [...state.packages[pIdx].features, ''] });
  const removeFeature = (pIdx, fIdx) =>
    updatePackage(pIdx, {
      features: state.packages[pIdx].features.filter((_, i) => i !== fIdx),
    });

  const markdown = useMemo(() => {
    const lines = [`# ${state.name}`, ''];
    for (const p of state.packages) {
      const sym = CURRENCY_SYMBOL[p.currency] || p.currency || '';
      lines.push(
        `## ${p.name}${p.isPopular ? '  ⭐' : ''} — ${sym}${p.price}/${p.billing || 'mo'}`,
      );
      if (p.audience) lines.push(`_${p.audience}_`);
      if (p.description) lines.push(p.description);
      lines.push('');
      for (const f of p.features || []) {
        if (f.trim()) lines.push(`- ${f}`);
      }
      lines.push('');
    }
    return lines.join('\n');
  }, [state]);

  const onCopy = async () => {
    await navigator.clipboard.writeText(markdown);
    setCopied(true);
    setTimeout(() => setCopied(false), 1800);
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-3 min-w-0">
          <button className="btn-icon" onClick={onClose}>
            <ArrowLeft className="w-4 h-4" />
          </button>
          <span className="flex-none h-10 w-10 rounded-xl bg-brand-50 text-brand-600 flex items-center justify-center">
            <Package className="w-5 h-5" />
          </span>
          <div className="min-w-0">
            <div className="text-xs uppercase tracking-wide text-ink-400">
              {state.savedId ? 'Editing menu' : 'New service menu'}
            </div>
            <input
              className="input"
              value={state.name}
              onChange={(e) => setState({ ...state, name: e.target.value })}
            />
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button className="btn-secondary" onClick={onCopy}>
            {copied ? <CheckCircle2 className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
            {copied ? 'Copied' : 'Copy as Markdown'}
          </button>
          <button className="btn-primary" onClick={onSave} disabled={saving}>
            <Save className="w-4 h-4" /> {saving ? 'Saving…' : state.savedId ? 'Update' : 'Save'}
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {state.packages.map((p, idx) => (
          <PackageCard
            key={idx}
            pkg={p}
            onChange={(patch) => updatePackage(idx, patch)}
            onRemove={() => removePackage(idx)}
            onFeatureChange={(fIdx, v) => updateFeature(idx, fIdx, v)}
            onAddFeature={() => addFeature(idx)}
            onRemoveFeature={(fIdx) => removeFeature(idx, fIdx)}
          />
        ))}
        <button
          className="rounded-2xl border-2 border-dashed border-gray-200 bg-white/40 hover:border-brand-300 hover:bg-brand-50/30 transition-colors p-6 flex flex-col items-center justify-center gap-2 text-ink-400 hover:text-brand-600 min-h-[200px]"
          onClick={addPackage}
        >
          <Plus className="w-6 h-6" />
          <span className="text-sm font-medium">Add tier</span>
        </button>
      </div>
    </div>
  );
}

function PackageCard({
  pkg,
  onChange,
  onRemove,
  onFeatureChange,
  onAddFeature,
  onRemoveFeature,
}) {
  const sym = CURRENCY_SYMBOL[pkg.currency] || pkg.currency || '$';
  return (
    <div
      className={clsx(
        'card relative',
        pkg.isPopular && 'ring-2 ring-brand-300 shadow-lg',
      )}
    >
      {pkg.isPopular && (
        <span className="absolute -top-3 left-1/2 -translate-x-1/2 pill-brand">
          ⭐ Most popular
        </span>
      )}
      <div className="flex items-start justify-between gap-2 mb-3">
        <input
          className="input font-semibold text-lg"
          value={pkg.name}
          onChange={(e) => onChange({ name: e.target.value })}
        />
        <button
          className="btn-icon hover:!text-rose-600 hover:!bg-rose-50"
          onClick={onRemove}
          title="Remove tier"
        >
          <Trash2 className="w-4 h-4" />
        </button>
      </div>

      <div className="flex items-end gap-1 mb-3">
        <span className="text-ink-400 text-lg font-semibold mb-1">{sym}</span>
        <input
          className="input text-3xl font-bold !p-1 !pl-2 max-w-[8rem]"
          type="number"
          value={pkg.price}
          onChange={(e) => onChange({ price: Number(e.target.value) || 0 })}
        />
        <span className="text-ink-400 text-sm mb-1.5">/</span>
        <input
          className="input text-sm !p-1 !pl-2 max-w-[6rem]"
          value={pkg.billing || 'monthly'}
          onChange={(e) => onChange({ billing: e.target.value })}
        />
      </div>

      <input
        className="input text-xs mb-2"
        value={pkg.audience || ''}
        placeholder="Audience (Small business / Agency partner …)"
        onChange={(e) => onChange({ audience: e.target.value })}
      />
      <textarea
        className="input text-sm min-h-[60px] mb-3"
        value={pkg.description || ''}
        placeholder="One-line description"
        onChange={(e) => onChange({ description: e.target.value })}
      />

      <div className="space-y-2">
        {(pkg.features || []).map((f, fIdx) => (
          <div key={fIdx} className="flex items-center gap-2">
            <CheckCircle2 className="w-4 h-4 flex-none text-emerald-600" />
            <input
              className="input text-xs flex-1"
              value={f}
              onChange={(e) => onFeatureChange(fIdx, e.target.value)}
            />
            <button
              className="btn-icon h-7 w-7 hover:!text-rose-600 hover:!bg-rose-50"
              onClick={() => onRemoveFeature(fIdx)}
            >
              <X className="w-3.5 h-3.5" />
            </button>
          </div>
        ))}
        <button className="btn-secondary w-full" onClick={onAddFeature}>
          <Plus className="w-4 h-4" /> Add feature
        </button>
      </div>

      <label className="mt-3 flex items-center gap-2 text-sm text-ink-700 cursor-pointer">
        <input
          type="checkbox"
          checked={!!pkg.isPopular}
          onChange={(e) => onChange({ isPopular: e.target.checked })}
          className="h-4 w-4 rounded border-gray-300 text-brand-600 focus:ring-brand-500/30"
        />
        Mark as "Most popular"
      </label>
    </div>
  );
}

/* =====================  Shared bits  ===================== */

function SavedList({ title, items, icon: Icon, getLabel, onOpen, onDelete }) {
  return (
    <div className="card">
      <div className="section-header">
        <div className="section-title">
          <span className="section-icon">
            <Icon className="w-4 h-4" />
          </span>
          {title}
        </div>
        <span className="text-xs text-ink-400">
          {items.length} {items.length === 1 ? 'item' : 'items'}
        </span>
      </div>
      <ul className="divide-y divide-gray-50">
        {items.map((it) => (
          <li
            key={it._id}
            className="py-2.5 flex items-center justify-between gap-3 first:pt-0 last:pb-0"
          >
            <button
              className="flex items-center gap-3 min-w-0 flex-1 text-left hover:opacity-90"
              onClick={() => onOpen(it)}
            >
              <span className="flex-none h-8 w-8 rounded-lg bg-brand-50 text-brand-600 flex items-center justify-center">
                <Icon className="w-4 h-4" />
              </span>
              <div className="min-w-0">
                <div className="font-medium text-ink-900 truncate">{it.name}</div>
                <div className="text-xs text-ink-400 truncate">
                  {getLabel(it)} · updated {formatRelative(it.updatedAt)}
                </div>
              </div>
            </button>
            <button
              className="btn-icon hover:!text-rose-600 hover:!bg-rose-50 flex-none"
              onClick={() => onDelete(it._id)}
              title="Delete"
            >
              <Trash2 className="w-4 h-4" />
            </button>
          </li>
        ))}
      </ul>
    </div>
  );
}
